import { useCallback, useMemo, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { FilterAltRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { useNavigate } from "react-router-dom";
import TanstackDatagrid from "components/ReusableComponents/TanstackDatagrid/TanstackDatagrid";
import { selectedSalesOrderPath } from "components/PimDashboard/data";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { soUrlKey } from "../CreateSalesOrder/data";
import { SoGinesysSyncBadge } from "../CreateSalesOrder/soUi";
import FilterModal from "../AllSalesOrders/FilterModal/FilterModal";
import {
	getSoSyncLogFilterColumns,
	listSalesOrderSyncLogsApi,
	lsSoSyncLogFilterValStr,
	soSyncLogEventTone,
	soSyncLogTableColumns,
} from "./data";

const SoSyncEventBadge = ({ value }) => {
	const key = value || "QUEUED";
	const tone = soSyncLogEventTone[key] || soSyncLogEventTone.QUEUED;
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				px: 1.1,
				py: 0.35,
				borderRadius: "8px",
				fontSize: 12,
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: tone.color,
				bgcolor: tone.bgcolor,
				width: "fit-content",
			}}>
			{key}
		</Box>
	);
};

const SoSyncLogs = () => {
	const navigate = useNavigate();
	const { setLoadingOpen, snackBar, setSnackBarOpen, setSnackBar } = GlobalState();
	const [data, setData] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [filterModal, setFilterModal] = useState(false);
	const [filterColumns, setFilterColumns] = useState(() => {
		if (!localStorage.getItem(lsSoSyncLogFilterValStr)) return [];
		const localStorageObj = JSON.parse(localStorage.getItem(lsSoSyncLogFilterValStr));
		if (typeof localStorageObj !== "object" || !localStorageObj) return [];
		return getSoSyncLogFilterColumns().filter((row) => localStorageObj[row.key] !== undefined);
	});
	const [filterColumnVal, setFilterColumnVal] = useState(() => {
		if (localStorage.getItem(lsSoSyncLogFilterValStr)) {
			return JSON.parse(localStorage.getItem(lsSoSyncLogFilterValStr));
		}
		return {};
	});

	const filterColumnOptions = useMemo(() => getSoSyncLogFilterColumns(), []);

	const tableColumns = useMemo(
		() =>
			soSyncLogTableColumns.map((col) => {
				if (col.key === "event_type") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<SoSyncEventBadge value={row.event_type} />
							</Box>
						),
					};
				}
				if (col.key === "ginesys_sync") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Box width="100%" display="flex" justifyContent="center">
								<SoGinesysSyncBadge value={row.ginesys_sync} />
							</Box>
						),
					};
				}
				if (col.key === "created_at") {
					return {
						...col,
						renderCell: ({ row }) => {
							if (!row.created_at) return "—";
							const d = new Date(row.created_at);
							return Number.isNaN(d.getTime()) ? String(row.created_at) : d.toLocaleString();
						},
					};
				}
				if (col.key === "error_message") {
					return {
						...col,
						renderCell: ({ row }) => (
							<Typography
								variant="body2"
								sx={{
									maxWidth: 220,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
									mx: "auto",
								}}
								title={row.error_message || ""}>
								{row.error_message || "—"}
							</Typography>
						),
					};
				}
				return col;
			}),
		[]
	);

	const fetchData = useCallback(
		async (limit, offset, filter) => {
			const isFirstLoad = data.length === 0;
			if (isFirstLoad) setLoadingOpen(true);
			try {
				const result = await listSalesOrderSyncLogsApi({ limit, offset, filter });
				return {
					rows: result.rows || [],
					total_rows_count: result.total_rows_count ?? 0,
				};
			} catch (error) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					severity: "error",
					message: parseErrorMessage(error),
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				return { rows: [], total_rows_count: 0 };
			} finally {
				if (isFirstLoad) setLoadingOpen(false);
			}
		},
		[data.length, setLoadingOpen, setSnackBar, setSnackBarOpen, snackBar]
	);

	const handleRowClick = ({ row }) => {
		if (!row?.sales_order_id) return;
		navigate(`/${selectedSalesOrderPath}?${soUrlKey}=${row.sales_order_id}`);
	};

	return (
		<Box
			sx={{
				height: "calc(100vh - 135px)",
				p: 1,
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				gap: 1,
			}}>
			<Box display="flex" columnGap={1} pt={0.5} flexWrap="wrap" alignItems="center" rowGap="5px">
				<Button
					variant="contained"
					size="small"
					startIcon={<FilterAltRounded />}
					onClick={() => setFilterModal(true)}
					sx={{ textTransform: "capitalize" }}>
					Filter
				</Button>
				:
				{!filterColumns?.length && (
					<Typography variant="body1" fontSize={16} color={grey[700]}>
						None
					</Typography>
				)}
				{filterColumns?.map((row) => (
					<Chip
						key={row.key}
						label={`${row.name} : ${
							Array.isArray(filterColumnVal[row.key])
								? filterColumnVal[row.key]?.join(", ")
								: filterColumnVal?.[row.key]
						}`}
						size="small"
					/>
				))}
			</Box>

			<Box sx={{ flex: 1, minHeight: 0 }}>
				<TanstackDatagrid
					columns={tableColumns}
					pageData={data}
					setPageData={setData}
					rowCount={rowCount}
					setRowCount={setRowCount}
					filterObj={filterColumnVal}
					setFilterObj={setFilterColumnVal}
					disableColumnFilter
					disableColumnSort
					disablePagination
					isLoading={false}
					emptyMessage="No SO sync logs found"
					handleRowClick={handleRowClick}
					getServerSideData={fetchData}
					getRowId={(row) => row.id}>
					<Box sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}>
						<Typography variant="body2" color="text.secondary">
							{rowCount ? `${rowCount} sync log${rowCount === 1 ? "" : "s"}` : "SO sync logs"}
						</Typography>
					</Box>
				</TanstackDatagrid>
			</Box>

			{filterModal && (
				<FilterModal
					modal={filterModal}
					handleCloseModal={() => setFilterModal(false)}
					filterColumns={filterColumns}
					setFilterColumns={setFilterColumns}
					filterColumnVal={filterColumnVal}
					setFilterColumnVal={setFilterColumnVal}
					localStorageStr={lsSoSyncLogFilterValStr}
					allColumns={filterColumnOptions}
				/>
			)}
		</Box>
	);
};

export default SoSyncLogs;
